import { Texture, TextureSource } from "pixi.js";
import type { Bitmap } from "@bd/art";

TextureSource.defaultOptions.scaleMode = "nearest";

const cache = new Map<number, Texture>();

/** Bitmap -> Pixi texture, cached by pixel hash so identical sprites share one texture. */
export function tex(b: Bitmap, key?: number): Texture {
  const k = key ?? b.hash();
  const hit = cache.get(k);
  if (hit) return hit;
  const canvas = document.createElement("canvas");
  canvas.width = b.w;
  canvas.height = b.h;
  const ctx = canvas.getContext("2d")!;
  const data = new ImageData(new Uint8ClampedArray(b.data), b.w, b.h);
  ctx.putImageData(data, 0, 0);
  const t = Texture.from(canvas);
  t.source.scaleMode = "nearest";
  cache.set(k, t);
  return t;
}

/** Cached texture by key, building the bitmap only on a miss (for sprites redrawn every frame). */
export function texLazy(key: number, make: () => Bitmap): Texture {
  return cache.get(key) ?? tex(make(), key);
}

/** Uncached texture for big one-off layers (base floor, light maps); call destroy() when replaced. */
export function freshTex(b: Bitmap): Texture {
  const canvas = document.createElement("canvas");
  canvas.width = b.w;
  canvas.height = b.h;
  canvas.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(b.data), b.w, b.h), 0, 0);
  const t = Texture.from(canvas);
  t.source.scaleMode = "nearest";
  return t;
}

/** Canvas data URL of a bitmap, for <img> tags in the HTML interface (box covers, portraits, room thumbs). */
const urlCache = new Map<number, string>();
export function dataUrl(b: Bitmap, scale = 1): string {
  const k = b.hash() * 31 + scale;
  const hit = urlCache.get(k);
  if (hit) return hit;
  const canvas = document.createElement("canvas");
  canvas.width = b.w * scale;
  canvas.height = b.h * scale;
  const ctx = canvas.getContext("2d")!;
  const tmp = document.createElement("canvas");
  tmp.width = b.w;
  tmp.height = b.h;
  tmp.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(b.data), b.w, b.h), 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(tmp, 0, 0, b.w * scale, b.h * scale);
  const url = canvas.toDataURL();
  urlCache.set(k, url);
  return url;
}
